import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeIcon, UserIcon, ClockIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import Helper from '../helpers';
import config from '../config';

const apiBaseUrl = config.apiBaseUrl;

const PTDirectoryEnquiries = () => {
    const [enquiries, setEnquiries] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [slug, setSlug] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchEnquiries = async () => {
            try {
                const token = await Helper.getCookie('wh-jwt');
                const response = await fetch(`${apiBaseUrl}/api/ptdirectory/my-enquiries`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                });

                if (response.ok) {
                    const data = await response.json();
                    setEnquiries(data.enquiries || []);
                    setTotalCount(data.totalCount || 0);
                    setSlug(data.slug || null);
                } else if (response.status === 401) {
                    setError('Please log in to view your enquiries.');
                } else {
                    setError('Failed to load enquiries. Please try again later.');
                }
            } catch {
                setError('Failed to load enquiries. Please check your connection.');
            } finally {
                setLoading(false);
            }
        };

        fetchEnquiries();
    }, []);

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-400"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white">PT Directory Enquiries</h1>
                    <p className="text-gray-400 mt-2">Potential clients who have contacted you via your PT Directory profile.</p>
                </div>

                {error && (
                    <div className="bg-red-900/20 border border-red-800/30 text-red-300 px-4 py-3 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                {/* Stats banner */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5">
                        <div className="flex items-center gap-3 mb-1">
                            <EnvelopeIcon className="h-6 w-6 text-emerald-400" />
                            <span className="text-gray-300 text-sm font-medium">Total Enquiries</span>
                        </div>
                        <p className="text-3xl font-bold text-white">{totalCount}</p>
                        <p className="text-gray-500 text-xs mt-1">potential clients have reached out</p>
                    </div>

                    {slug && (
                        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5">
                            <div className="flex items-center gap-3 mb-1">
                                <UserIcon className="h-6 w-6 text-blue-400" />
                                <span className="text-gray-300 text-sm font-medium">Your Public Profile</span>
                            </div>
                            <Link
                                to={`/pt/${slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-sm font-medium mt-1 transition-colors"
                            >
                                View your profile
                                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                            </Link>
                            <p className="text-gray-500 text-xs mt-1 break-all">/pt/{slug}</p>
                        </div>
                    )}
                </div>

                {/* Enquiries list */}
                {enquiries.length === 0 ? (
                    <div className="text-center py-16">
                        <EnvelopeIcon className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                        <p className="text-gray-300 text-xl font-semibold mb-2">No enquiries yet</p>
                        <p className="text-gray-500 text-sm mb-6">
                            When potential clients contact you through your profile, their details will appear here.
                        </p>
                        {slug ? (
                            <Link
                                to={`/pt/${slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors"
                            >
                                View Your Profile
                            </Link>
                        ) : (
                            <Link
                                to="/pt-directory-signup"
                                className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors"
                            >
                                Join the PT Directory
                            </Link>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {enquiries.map(enquiry => (
                            <div key={enquiry.id} className="bg-gray-900 border border-gray-700 rounded-xl p-5">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <UserIcon className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                                            <span className="text-white font-semibold">
                                                {enquiry.enquirerName || 'Anonymous'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <EnvelopeIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                            <a
                                                href={`mailto:${enquiry.enquirerEmail}`}
                                                className="text-emerald-400 hover:text-emerald-300 text-sm transition-colors"
                                            >
                                                {enquiry.enquirerEmail}
                                            </a>
                                        </div>
                                        {enquiry.message && (
                                            <div className="bg-gray-800/60 rounded-lg p-3 mt-2">
                                                <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{enquiry.message}</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-gray-500 text-xs flex-shrink-0">
                                        <ClockIcon className="h-3.5 w-3.5" />
                                        <span>{formatDate(enquiry.createdAt)}</span>
                                    </div>
                                </div>

                                <div className="mt-3 pt-3 border-t border-gray-700/50 flex gap-2">
                                    <a
                                        href={`mailto:${enquiry.enquirerEmail}?subject=Re: Your enquiry via WorldsHighStreet PT Directory`}
                                        className="inline-flex items-center gap-1.5 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 text-sm px-3 py-1.5 rounded-lg transition-colors border border-emerald-600/30"
                                    >
                                        <EnvelopeIcon className="h-3.5 w-3.5" />
                                        Reply
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PTDirectoryEnquiries;
